import { isValidElement, type MouseEventHandler, type ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { buildAnalyticsEventName, trackAnalyticsEvent } from '@/lib/analytics';
import { classNames } from '@/utils/classNames';

import styles from '@/styles/common.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

type BaseButtonProps = {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  title?: string;
  ariaLabel?: string;
};

type NativeButtonProps = BaseButtonProps & {
  type?: 'button' | 'submit' | 'reset';
  to?: never;
  href?: never;
};

type RouterLinkButtonProps = BaseButtonProps & {
  to: string;
  href?: never;
  external?: never;
};

type AnchorButtonProps = BaseButtonProps & {
  href: string;
  external?: boolean;
  to?: never;
  target?: string;
  rel?: string;
};

export type ButtonProps = NativeButtonProps | RouterLinkButtonProps | AnchorButtonProps;

const variantClassName: Record<ButtonVariant, string> = {
  primary: styles.buttonPrimary!,
  secondary: styles.buttonSecondary!,
  ghost: styles.buttonGhost!,
  danger: styles.buttonDanger!,
};

const sizeClassName: Record<ButtonSize, string> = {
  sm: styles.buttonSmall!,
  md: '',
  lg: styles.buttonLarge!,
};

function getButtonClassName(props: BaseButtonProps): string {
  return classNames(
    styles.button,
    props.variant ? variantClassName[props.variant] : variantClassName.primary,
    props.size ? sizeClassName[props.size] : sizeClassName.md,
    props.fullWidth && styles.buttonFullWidth,
    props.className,
  );
}

function extractTextContent(content: ReactNode): string {
  if (typeof content === 'string' || typeof content === 'number') {
    return String(content);
  }

  if (Array.isArray(content)) {
    return content.map(extractTextContent).join(' ');
  }

  if (isValidElement<{ children?: ReactNode }>(content)) {
    return extractTextContent(content.props.children);
  }

  return '';
}

function getButtonAnalyticsLabel(props: ButtonProps): string {
  const textContent = extractTextContent(props.children).trim();

  return props.ariaLabel ?? props.title ?? (textContent || 'action');
}

function getButtonAnalyticsScope(props: ButtonProps): string {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  return [currentPath, props.to ?? '', props.href ?? ''].filter(Boolean).join('|');
}

export function Button(props: ButtonProps): JSX.Element {
  const className = getButtonClassName(props);
  const variant = props.variant ?? 'primary';
  const size = props.size ?? 'md';
  const disabled = props.disabled ?? false;
  const analyticsEventName = buildAnalyticsEventName(getButtonAnalyticsLabel(props), getButtonAnalyticsScope(props));
  const handleClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    trackAnalyticsEvent(analyticsEventName);
    props.onClick?.(event);
  };

  if (props.to !== undefined) {
    const { to, children, title, ariaLabel } = props;

    return (
      <Link
        to={to}
        className={classNames(
          styles.button,
          variantClassName[variant],
          sizeClassName[size],
          props.fullWidth && styles.buttonFullWidth,
          props.className,
        )}
        onClick={handleClick}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
        title={title}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }

  if (props.href !== undefined) {
    const { href, external, children, target, rel, title, ariaLabel } = props;

    return (
      <a
        href={href}
        className={className}
        onClick={handleClick}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
        target={external ? '_blank' : target}
        rel={external ? 'noreferrer noopener' : rel}
        title={title}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }

  const { type = 'button', children, title, ariaLabel } = props;

  return (
    <button className={className} type={type} disabled={disabled} onClick={handleClick} title={title} aria-label={ariaLabel}>
      {children}
    </button>
  );
}
